// Pure route-planning logic, ported from the original route-planner.html.
// No DOM, no network: everything here is unit-tested in test/planner.test.js.

export const MAX_STOPS_PER_VAN = 23;
export const VAN_COLORS = ['#1a3c6e', '#b45309', '#7b2d8b', '#0f766e', '#b91c1c', '#4d7c0f'];

/** Bengaluru rush-hour multiplier applied to OSRM free-flow driving times. */
export function trafficFactor(minOfDay) {
  const h = (minOfDay / 60) % 24;
  if ((h >= 8 && h < 11) || (h >= 17 && h < 21)) return 1.7; // peak
  if (h >= 11 && h < 17) return 1.4; // daytime
  return 1.15; // early morning / night
}

export function fmtClock(m) {
  m = ((Math.round(m) % 1440) + 1440) % 1440;
  const h = Math.floor(m / 60), mm = String(m % 60).padStart(2, '0');
  return ((h % 12) || 12) + ':' + mm + ' ' + (h < 12 ? 'am' : 'pm');
}

export function fmtDuration(min) {
  min = Math.round(min);
  return min >= 60 ? Math.floor(min / 60) + ' h ' + (min % 60) + ' min' : min + ' min';
}

export function parseClock(hhmm, fallback = 480) {
  const m = /^(\d{1,2}):(\d{2})/.exec(hhmm || '');
  return m ? (+m[1]) * 60 + (+m[2]) : fallback;
}

/** Sort stops by compass angle around the depot, cutting the circle at the largest empty gap. */
export function sweepSort(depot, stops) {
  if (stops.length < 2) return stops.slice();
  const withAng = stops.map(d => ({ d, ang: Math.atan2(d.lat - depot.lat, d.lng - depot.lng) }));
  withAng.sort((a, b) => a.ang - b.ang);
  let gapIdx = 0, gapMax = -1;
  for (let i = 0; i < withAng.length; i++) {
    const next = withAng[(i + 1) % withAng.length];
    let gap = next.ang - withAng[i].ang;
    if (gap < 0) gap += Math.PI * 2;
    if (gap > gapMax) { gapMax = gap; gapIdx = (i + 1) % withAng.length; }
  }
  return withAng.slice(gapIdx).concat(withAng.slice(0, gapIdx)).map(x => x.d);
}

const volOf = d => d.vol || 1;

/**
 * Split stops into vans. Manual van pins (d.van = 1..n) are honoured; the rest are
 * sweep-sorted and filled zone by zone so each van gets a similar total VOLUME.
 */
export function assignVans(depot, stops, nVans) {
  nVans = Math.max(1, nVans | 0);
  const clusters = Array.from({ length: nVans }, () => []);
  stops.forEach(d => { if (d.van >= 1 && d.van <= nVans) clusters[d.van - 1].push(d); });
  const auto = sweepSort(depot, stops.filter(d => !(d.van >= 1 && d.van <= nVans)));
  const target = stops.reduce((a, d) => a + volOf(d), 0) / nVans;
  let v = 0;
  auto.forEach(d => {
    while (v < nVans - 1 && clusters[v].reduce((a, x) => a + volOf(x), 0) + volOf(d) / 2 >= target) v++;
    clusters[v].push(d);
  });
  return clusters.map((stops, i) => ({ van: i + 1, stops })).filter(c => c.stops.length);
}

/**
 * Round-trip order starting and ending at index 0 (the depot).
 * "Early" stops are visited first; nearest-neighbour then 2-opt inside each phase.
 */
export function routeOrder(dist, earlySet = new Set()) {
  const n = dist.length;
  const visited = new Array(n).fill(false); visited[0] = true;
  const order = [0];
  const pickNearest = cands => {
    const last = order[order.length - 1];
    let best = -1, bd = Infinity;
    cands.forEach(j => { if (!visited[j] && dist[last][j] < bd) { bd = dist[last][j]; best = j; } });
    return best;
  };
  const earlyIdx = [], lateIdx = [];
  for (let i = 1; i < n; i++) (earlySet.has(i) ? earlyIdx : lateIdx).push(i);
  earlyIdx.forEach(() => { const b = pickNearest(earlyIdx); order.push(b); visited[b] = true; });
  lateIdx.forEach(() => { const b = pickNearest(lateIdx); order.push(b); visited[b] = true; });
  const tourLen = o => { let t = 0; for (let i = 0; i < o.length; i++) t += dist[o[i]][o[(i + 1) % o.length]]; return t; };
  const twoOpt = (lo, hi) => {
    let improved = true;
    while (improved) {
      improved = false;
      for (let i = lo; i < hi; i++) for (let j = i + 1; j <= hi; j++) {
        const cand = order.slice(0, i).concat(order.slice(i, j + 1).reverse(), order.slice(j + 1));
        if (tourLen(cand) < tourLen(order) - 1) { order.splice(0, order.length, ...cand); improved = true; }
      }
    }
  };
  const e = earlyIdx.length;
  if (e === 0) twoOpt(1, n - 1);
  else { if (e > 1) twoOpt(1, e); if (n - 1 - e > 1) twoOpt(e + 1, n - 1); }
  return order;
}

/**
 * ETAs (minutes after midnight) for each point of a round trip.
 * legs = OSRM route legs ({duration} in seconds); etas[0] = departure, last = back at depot.
 */
export function computeEtas(legs, departMin, unloadMin) {
  let t = departMin;
  const etas = [departMin];
  legs.forEach((leg, li) => {
    t += (leg.duration / 60) * trafficFactor(t);
    etas.push(t);
    if (li < legs.length - 1) t += unloadMin;
  });
  return etas;
}

/** Google Maps turn-by-turn link to one destination (opens the Maps app on Android). */
export function navLink(lat, lng) {
  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;
}

/** Validate a plan request; returns an error string or null. */
export function validatePlan(stops, nVans) {
  if (!stops.length) return 'Tick at least one stop.';
  if (stops.length > MAX_STOPS_PER_VAN * nVans) return `Too many stops (max ${MAX_STOPS_PER_VAN * nVans} for ${nVans} van${nVans > 1 ? 's' : ''}).`;
  const missing = stops.filter(s => !Number.isFinite(s.lat) || !Number.isFinite(s.lng));
  if (missing.length) return 'No GPS for: ' + missing.map(s => s.name).join(', ') + '. Fix them under Locations.';
  return null;
}
